import {Component, OnInit} from '@angular/core';
import {SaleProductModel} from "../../../api/entity/saleProduct.model";
import {IDialogComponent} from "../../../../../../common/dialog/iDialog.component";
import {DialogWithInput} from "../../../../../../common/dialog/dialogWithInput";
import {CommonModule} from "@angular/common";
import {DialogModule} from "../../../../../../common/dialog/dialog.module";
import {
  SaleSaveProductFormModule
} from "../../../../../api/component/sale-save-product-form/sale-save-product-form.module";
import {FormBuilder, FormGroup, UntypedFormControl, Validators} from "@angular/forms";
import {AmountInputModule} from "../../../../../../../components/common/amount/amount-input/amount-input.module";
import {FormFieldModule} from "../../../../../../core/component/form-field/form-field.module";
import {NumberInputModule} from "../../../../../../../components/common/number/number-input/number-input.module";
import {PriceLabelModule} from "../../../../../../common/price/api/component/price-label/price-label.module";
import {SaleUpdateSaleProductActionService} from "../../../../../impl/service/saleUpdateSaleProductAction.service";
import {ButtonModule} from "../../../../../../core/component/button/button.module";
import {BaseView} from "../../../../../../core/view/base.view";
import {SaleProductSummaryModel} from "../../../api/entity/saleProductSummary.model";
import {CardModule} from "../../../../../../core/component/card/card.module";

export class ProductUpdatePriceDialogInput {
  entity: SaleProductModel;
  saleId: number;
}

export class ProductUpdatePriceDialogOutput {
  entity: SaleProductSummaryModel;
}

@Component({
  selector: 'app-sale-product-update-price-dialog',
  templateUrl: './sale-product-update-price-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    SaleSaveProductFormModule,
    AmountInputModule,
    FormFieldModule,
    NumberInputModule,
    PriceLabelModule,
    ButtonModule,
    CardModule,
  ],
  providers: [
    SaleUpdateSaleProductActionService
  ]
})
export class SaleProductUpdatePriceDialogComponent extends BaseView implements IDialogComponent<ProductUpdatePriceDialogInput, ProductUpdatePriceDialogOutput>,
 OnInit {
  dialog: DialogWithInput<ProductUpdatePriceDialogInput, ProductUpdatePriceDialogOutput>;
  formGroup: FormGroup;

  constructor(private formBuilder: FormBuilder,
              public saleUpdateSaleProductActionService: SaleUpdateSaleProductActionService) {
    super();
    this.formGroup = this.formBuilder.group({
      quantity: this.formBuilder.control(1, Validators.required),
      taxPercentage: this.formBuilder.control(0, Validators.required),
      unitPriceVatExcluded: this.formBuilder.control(0, Validators.required),
      unitPriceVatIncluded: this.formBuilder.control(0, Validators.required),
      totalPriceVatIncluded: this.formBuilder.control(0, Validators.required),
      totalPriceVatExcluded: this.formBuilder.control(0, Validators.required),
    });

    this.taxPercentageControl.valueChanges.subscribe((value) => {
      this.updateUnitPriceVatIncluded();
      this.updateTotalPriceVatIncluded();
      this.updateTotalPriceVatExcluded();
      this.taxPercentageControl.setValue(this.roundNumber(value), {emitEvent: false});
    });

    this.unitPriceVatExcluded.valueChanges.subscribe((value) => {
      this.updateUnitPriceVatIncluded();
      this.unitPriceVatExcluded.setValue(this.roundNumber(value), {emitEvent: false});
      this.updateTotalPriceVatIncluded();
      this.updateTotalPriceVatExcluded();
    });
    this.unitPriceVatIncluded.valueChanges.subscribe((value) => {
      this.updateUnitPriceVatExcluded();
      this.unitPriceVatIncluded.setValue(this.roundNumber(value), {emitEvent: false});
      this.updateTotalPriceVatIncluded();
      this.updateTotalPriceVatExcluded();
    });
    this.quantityControl.valueChanges.subscribe((value) => {
      this.quantityControl.setValue(Math.round(value), {emitEvent: false});
      this.updateTotalPriceVatIncluded();
      this.updateTotalPriceVatExcluded();

    });
  }

  ngOnInit() {
    this.formGroup.patchValue({
      quantity: this.dialog.input.entity.summary.quantity,
      taxPercentage: this.dialog.input.entity.summary.vatRate,
      unitPriceVatExcluded: this.dialog.input.entity.summary.unitPriceVatExcluded,
      unitPriceVatIncluded: this.dialog.input.entity.summary.unitPriceVatIncluded,
    });
    this.updateTotalPriceVatIncluded();
    this.updateTotalPriceVatExcluded();
  }

  update() {
    this.saleUpdateSaleProductActionService.executeAction({
      id: this.dialog.input.saleId,
      saleProduct: {
        productId: this.dialog.input.entity.summary.product.id,
        comment: this.dialog.input.entity.summary.comment,
        id: this.dialog.input.entity.summary.id,
        taxPercentage: this.formGroup.value.taxPercentage,
        unitPriceVatExcluded: this.formGroup.value.unitPriceVatExcluded,
        unitPriceVatIncluded: this.formGroup.value.unitPriceVatIncluded,
        quantity: this.formGroup.value.quantity,
        totalPriceVatExcluded: this.formGroup.value.totalPriceVatExcluded,
        totalPriceVatIncluded: this.formGroup.value.totalPriceVatIncluded
      }
    }).then(value => this.dialog.close({
      entity: value
    }))
        .catch(e => this.toastControl.presentError(e));
  }

  updateUnitPriceVatIncluded() {
    const taxPercentage = this.taxPercentageControl.value;
    const price = this.unitPriceVatExcluded.value * (1 + (taxPercentage / 100));
    this.unitPriceVatIncluded.setValue(this.roundNumber(price), {emitEvent: false});
  }

  updateTotalPriceVatIncluded() {
    const price = this.quantityControl.value * this.unitPriceVatIncluded.value;
    this.totalPriceVatIncludedControl.setValue(this.roundNumber(price), {emitEvent: false});
  }

  updateTotalPriceVatExcluded() {
    const price = this.quantityControl.value * this.unitPriceVatExcluded.value;
    this.totalPriceVatExcludedControl.setValue(this.roundNumber(price), {emitEvent: false});
  }

  updateUnitPriceVatExcluded() {
    const taxPercentage = this.taxPercentageControl.value;
    const ratio = 1 + (taxPercentage / 100);
    const calculatedValue = ratio !== 0 ? (this.unitPriceVatIncluded.value / ratio) : 0;
    this.unitPriceVatExcluded.setValue(this.roundNumber(calculatedValue), {emitEvent: false});
  }

  private roundNumber(nb: number): number {
    return Math.round(nb * 100) / 100;
  }

  get taxPercentageControl(): UntypedFormControl {
    return this.formGroup.controls.taxPercentage as UntypedFormControl;
  }

  get totalPriceVatIncludedControl(): UntypedFormControl {
    return this.formGroup.controls.totalPriceVatIncluded as UntypedFormControl;
  }

  get unitPriceVatIncluded(): UntypedFormControl {
    return this.formGroup.controls.unitPriceVatIncluded as UntypedFormControl;
  }

  get unitPriceVatExcluded(): UntypedFormControl {
    return this.formGroup.controls.unitPriceVatExcluded as UntypedFormControl;
  }

  get quantityControl(): UntypedFormControl {
    return this.formGroup.controls.quantity as UntypedFormControl;
  }

  get totalPriceVatExcludedControl(): UntypedFormControl {
    return this.formGroup.controls.totalPriceVatExcluded as UntypedFormControl;
  }

}
